import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { supabase } from '../services/supabaseAudioService';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import InterfaceLanguageToggle from './InterfaceLanguageToggle';
import './MobileLoginPage.css';

import { EmojiText } from './common/Picto';
interface MobileLoginPageProps {
  onLogin: (response: any) => void;
  onContinueAsGuest: () => void;
}

const LANGS = [
  { name: 'WOLOF' },
  { name: 'BAMBARA' },
  { name: 'PEUL' },
  { name: 'LINGALA' },
  { name: 'SWAHILI' },
  { name: 'COPTE' },
];

const MobileLoginPage: React.FC<MobileLoginPageProps> = ({ onLogin, onContinueAsGuest }) => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const isDark = theme === 'dark';

  const VOCAB_ROWS = [
    { fr: t('landing.vocabWords.hello'),     wf: 'Salamalekum',    ba: 'I ni ce',     la: 'Mbote'         },
    { fr: t('landing.vocabWords.thanks'),    wf: 'Jërejëf',         ba: 'Aw ni baara', la: 'Matondo'       },
    { fr: t('landing.vocabWords.goodbye'),   wf: 'Ba beneen yoon',  ba: 'Kà taa',      la: 'Tokomonana'    },
    { fr: t('landing.vocabWords.doingWell'), wf: 'Maa ngi fi rekk', ba: 'N ka kɛnɛ',   la: 'Nazali malamu' },
    { fr: t('landing.vocabWords.water'),     wf: 'Ndox',            ba: 'Ji',          la: 'Mai'           },
    { fr: t('landing.vocabWords.family'),    wf: 'Mbokk yi',        ba: 'Somɔgɔw',     la: 'Libota'        },
  ];

  const FEATURES = [
    { icon: '🗂️', title: t('landing.features.flashcards.title'),    desc: t('mobileLogin.features.flashcards') },
    { icon: '✏️', title: t('landing.features.quiz.title'),          desc: t('mobileLogin.features.quiz') },
    { icon: '💬', title: t('landing.features.conversations.title'), desc: t('mobileLogin.features.conversations') },
    { icon: '📖', title: t('landing.features.grammar.title'),       desc: t('mobileLogin.features.grammar') },
    { icon: '🎵', title: t('landing.features.songs.title'),         desc: t('mobileLogin.features.songs') },
    { icon: '🏆', title: t('landing.features.progression.title'),   desc: t('mobileLogin.features.progression') },
  ];
  const [loading, setLoading] = useState(false);
  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const isNative = Capacitor.isNativePlatform();
      const redirectUrl = isNative ? 'com.jogolinga.com://login-callback' : window.location.origin;
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: { access_type: 'offline', prompt: 'consent' },
          skipBrowserRedirect: isNative,
        },
      });
      if (error) throw error;
      if (isNative && data?.url) {
        await Browser.open({ url: data.url, windowName: '_blank', presentationStyle: 'fullscreen', toolbarColor: 'var(--gold-primary)' });
      }
    } catch {
      // silencieux
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`mlp ${isDark ? 'mlp--dark' : 'mlp--light'}`}>

      {/* ── Splash (plein écran) ── */}
      <div className="mlp__splash">
        <InterfaceLanguageToggle dark={isDark} className="mlp__lang-toggle" />
        <motion.button
          type="button"
          className="mlp__theme-btn"
          onClick={toggleTheme}
          aria-label={(isDark ? t('mobileLogin.themeLight') : t('mobileLogin.themeDark')) as string}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <EmojiText text={isDark ? '☀️' : '🌙'} />
        </motion.button>

        <div className="mlp__deco-top">◇ · 𓂀 · ◇ · 𓇳 · ◇ · 𓏠 · ◇</div>

        <motion.div
          className="mlp__inner"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          {/* Logo « bois brûlé » */}
          <motion.img
            src="/assets/logo-tile.svg"
            alt=""
            className="mlp__logo"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />

          <h1 className="mlp__title">JogoLinga</h1>
          <p className="mlp__subtitle">{t('mobileLogin.subtitle')}</p>

          {/* Langues — noms sans drapeaux */}
          <div className="mlp__langs">
            {LANGS.map(l => (
              <span key={l.name} className="mlp__lang-name">{l.name}</span>
            ))}
          </div>

          {/* Bouton Google */}
          <motion.button
            className="mlp__btn-google"
            onClick={handleGoogleSignIn}
            disabled={loading}
            whileTap={{ scale: 0.97 }}
            whileHover={{ scale: 1.02 }}
          >
            {loading ? (
              <span className="mlp__spinner" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" style={{ flexShrink: 0 }}>
                <path fill="currentColor" d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z"/>
                <path fill="currentColor" d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.1.83-.64 2.08-1.84 2.92l2.84 2.2c1.7-1.57 2.68-3.88 2.68-6.62z"/>
                <path fill="currentColor" d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z"/>
                <path fill="currentColor" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.84-2.2c-.76.53-1.78.9-3.12.9-2.38 0-4.4-1.57-5.12-3.74L.97 13.04C2.45 15.98 5.48 18 9 18z"/>
              </svg>
            )}
            <span>{loading ? t('mobileLogin.connecting') : t('mobileLogin.continueGoogle')}</span>
          </motion.button>

          {/* Séparateur */}
          <div className="mlp__sep"><span>{t('landing.hero.or')}</span></div>

          {/* Bouton invité */}
          <motion.button
            className="mlp__btn-guest"
            onClick={onContinueAsGuest}
            whileTap={{ scale: 0.97 }}
          >
            {t('landing.hero.continueGuest')}
          </motion.button>
        </motion.div>

        <div className="mlp__deco-bottom">◁ · ◇ · ▷ · ◇ · ◁ · ◇ · ▷</div>
      </div>

      {/* ── Contenu éditorial (scrollable) ── */}
      <div className="mlp__editorial">

        {/* Pourquoi */}
        <section className="mlp__section">
          <h2 className="mlp__section-title">{t('mobileLogin.why.title')}</h2>
          <p className="mlp__intro-text">
            {t('mobileLogin.why.textBefore')}<strong>{t('mobileLogin.why.textStrong')}</strong>{t('mobileLogin.why.textAfter')}
          </p>
        </section>

        {/* Guides langues */}
        <section className="mlp__section">
          <h2 className="mlp__section-title">{t('landing.guides.title')}</h2>
          <div className="mlp__guides-list">
            <Link to="/guide/wolof" className="mlp__guide-card">
              <strong className="mlp__guide-title">{t('landing.guides.wolofTitle')}</strong>
              <p className="mlp__guide-desc">
                {t('mobileLogin.guides.wolofDesc')}
              </p>
              <span className="mlp__guide-link">{t('landing.guides.cta')}</span>
            </Link>
            <Link to="/guide/bambara" className="mlp__guide-card">
              <strong className="mlp__guide-title">{t('landing.guides.bambaraTitle')}</strong>
              <p className="mlp__guide-desc">
                {t('mobileLogin.guides.bambaraDesc')}
              </p>
              <span className="mlp__guide-link">{t('landing.guides.cta')}</span>
            </Link>
            <Link to="/guide/swahili" className="mlp__guide-card">
              <strong className="mlp__guide-title">{t('landing.guides.swahiliTitle')}</strong>
              <p className="mlp__guide-desc">
                {t('mobileLogin.guides.swahiliDesc')}
              </p>
              <span className="mlp__guide-link">{t('landing.guides.cta')}</span>
            </Link>
            <Link to="/apprendre" className="mlp__guide-card">
              <strong className="mlp__guide-title">{t('landing.guides.learnTitle')}</strong>
              <p className="mlp__guide-desc">
                {t('landing.guides.learnDesc')}
              </p>
              <span className="mlp__guide-link">{t('landing.guides.learnCta')}</span>
            </Link>
          </div>
        </section>

        {/* Tableau vocabulaire */}
        <section className="mlp__section">
          <h2 className="mlp__section-title">{t('mobileLogin.vocabTitle')}</h2>
          <div className="mlp__table-wrap">
            <table className="mlp__vocab-table">
              <thead>
                <tr>
                  <th>{t('landing.vocabTable.colFrench')}</th>
                  <th>Wolof</th>
                  <th>Bambara</th>
                  <th>Lingala</th>
                </tr>
              </thead>
              <tbody>
                {VOCAB_ROWS.map(r => (
                  <tr key={r.fr}>
                    <td>{r.fr}</td>
                    <td>{r.wf}</td>
                    <td>{r.ba}</td>
                    <td>{r.la}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Méthode */}
        <section className="mlp__section">
          <h2 className="mlp__section-title">{t('mobileLogin.methodTitle')}</h2>
          <p className="mlp__intro-text" style={{ marginBottom: '0.8rem' }}>
            {t('mobileLogin.methodTextBefore')}<strong>{t('mobileLogin.methodTextStrong')}</strong>{t('mobileLogin.methodTextAfter')}
          </p>
          <div className="mlp__features-grid">
            {FEATURES.map(f => (
              <div key={f.title} className="mlp__feature-card">
                <span className="mlp__feature-icon"><EmojiText text={f.icon} /></span>
                <strong className="mlp__feature-title">{f.title}</strong>
                <p className="mlp__feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Footer liens */}
        <footer className="mlp__footer-links">
          <Link to="/apprendre">{t('landing.footer.learn')}</Link>
          <span>·</span>
          <Link to="/articles">Articles</Link>
          <span>·</span>
          <Link to="/about">{t('landing.footer.about')}</Link>
          <span>·</span>
          <Link to="/privacy">{t('landing.footer.privacy')}</Link>
          <span>·</span>
          <Link to="/terms">{t('mobileLogin.footerTerms')}</Link>
        </footer>

      </div>

    </div>
  );
};

export default MobileLoginPage;
